import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Page } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/plans";
import { toast } from "sonner";
import { User, Store, Save, ShieldCheck, Mail, Briefcase, ArrowRight, Lock, Database, Trash2 } from "lucide-react";

const SECTORS = [
  { id: "supermercado", label: "Supermercados & Mercearia" },
  { id: "moda", label: "Moda & Vestuário" },
  { id: "farmacia", label: "Farmácia & Saúde" },
  { id: "petshop", label: "Pet Shop" },
  { id: "eletronicos", label: "Eletrônicos & Tecnologia" },
  { id: "casa", label: "Casa & Decoração" },
  { id: "beleza", label: "Beleza & Cosméticos" },
  { id: "bebidas", label: "Bebidas & Adega" },
  { id: "padaria", label: "Padaria & Confeitaria" },
  { id: "construcao", label: "Construção & Reforma" },
  { id: "outros", label: "Outros / Varejo Geral" },
];

const STATES = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setForm({
        name: r.data.name || "",
        company: r.data.company || "",
        sector: r.data.sector || "outros",
        cnpj: r.data.cnpj || "",
        phone: r.data.phone || "",
        address: r.data.address || "",
        city: r.data.city || "",
        state: r.data.state || "",
      });
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/profile", form);
      // Update context cache
      const cached = JSON.parse(localStorage.getItem("demandai_user") || "{}");
      const next = { ...cached, ...r.data };
      localStorage.setItem("demandai_user", JSON.stringify(next));
      if (setUser) setUser(next);
      toast.success("Perfil atualizado com sucesso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Skeleton className="h-96" />;

  const canManageStores = hasAccess(user, "stores");

  return (
    <Page title="Perfil & Loja" subtitle="Mantenha seus dados atualizados — usamos o setor para personalizar benchmarking e recomendações.">
      {/* Multi-store quick access banner */}
      <div className="mb-6 p-5 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
            <Store className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold flex items-center gap-2">
              Tem mais de uma loja?
              {!canManageStores && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              {canManageStores
                ? "Cadastre filiais com CNPJ, endereço e setor próprios e transfira estoque entre lojas."
                : "Disponível no plano Pro — cadastre filiais e transfira estoque entre lojas."}
            </div>
          </div>
        </div>
        <Link to={canManageStores ? "/app/stores" : "/app/pricing"}>
          <Button
            data-testid="profile-stores-cta"
            variant="outline"
            className="border-primary/40 text-primary hover:bg-primary/10 h-11 px-5 touch-manipulation"
          >
            {canManageStores ? "Gerenciar lojas" : "Ver plano Pro"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal data */}
        <div className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-5">
            <User className="w-4 h-4 text-primary" /> Dados pessoais
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Nome completo</Label>
              <Input data-testid="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label className="flex items-center gap-2"><Mail className="w-3 h-3" /> Email</Label>
              <Input value={user?.email || ""} disabled className="mt-2 opacity-70" />
              <div className="text-xs text-muted-foreground mt-1">Para alterar email, contate suporte.</div>
            </div>
            <div>
              <Label>Telefone</Label>
              <Input data-testid="profile-phone" placeholder="(11) 99999-9999" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
            </div>
          </div>
        </div>

        {/* Store data */}
        <div className="p-6 rounded-xl border border-primary/30 bg-card glow-coral lg:col-span-2">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-5">
            <Store className="w-4 h-4 text-primary" /> Dados da loja
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Razão social / Nome fantasia</Label>
              <Input data-testid="profile-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label>CNPJ</Label>
              <Input data-testid="profile-cnpj" placeholder="00.000.000/0000-00" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="mt-2 font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="flex items-center gap-2"><Briefcase className="w-3 h-3" /> Setor de atuação</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                <SelectTrigger data-testid="profile-sector" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1.5">
                ⓘ O setor define seu grupo de comparação no <a href="/app/benchmarking" className="text-primary hover:underline">Benchmarking</a> — escolha o que mais se aproxima do seu negócio.
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Endereço</Label>
              <Input placeholder="Rua, número, complemento" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label>Cidade</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Status card */}
        <div className="p-5 rounded-xl border border-border bg-card space-y-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-sm font-medium">Plano: <span className="text-primary">{user?.plan || "free"}</span></div>
              <div className="text-xs text-muted-foreground">Gerencie cobrança, cancele ou troque de plano com um clique.</div>
            </div>
          </div>
          {user?.stripe_customer_id && (
            <Button
              data-testid="billing-portal-btn"
              type="button"
              variant="outline"
              size="sm"
              disabled={demoLoading}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const r = await api.post("/billing/portal", { origin_url: window.location.origin });
                  if (r.data.portal_url) {
                    window.location.href = r.data.portal_url;
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Não foi possível abrir o portal de cobrança");
                }
              }}
              className="w-full border-primary/30 text-primary hover:bg-primary/10"
            >
              Gerenciar assinatura no Stripe <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Demo data tools (admin-friendly) */}
        <div className="lg:col-span-2 p-5 rounded-xl border border-amber-500/20 bg-amber-500/5 space-y-3">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-amber-400" />
            <div className="text-sm font-medium text-amber-300">Dados de demonstração</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Popule sua conta com produtos, vendas e lojas fictícias para apresentações. Pode ser removido a qualquer momento sem afetar dados reais importados via CSV ou API.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="seed-demo-btn"
              type="button"
              variant="outline"
              size="sm"
              disabled={demoLoading}
              onClick={async (e) => {
                e.preventDefault();
                setDemoLoading(true);
                try {
                  const r = await api.post("/me/seed-demo");
                  toast.success(`Demo carregado: ${r.data.products} produtos, ${r.data.sales} vendas`);
                  setTimeout(() => window.location.reload(), 800);
                } catch (err) {
                  toast.error("Falha ao carregar demo");
                } finally {
                  setDemoLoading(false);
                }
              }}
              className="border-amber-500/40 hover:bg-amber-500/10"
            >
              <Database className="w-4 h-4 mr-2" />
              {demoLoading ? "Carregando..." : "Carregar dados demo"}
            </Button>
            <Button
              data-testid="wipe-demo-btn"
              type="button"
              variant="outline"
              size="sm"
              disabled={demoLoading}
              onClick={async (e) => {
                e.preventDefault();
                if (!window.confirm("Remover APENAS os dados demo? Dados reais importados serão preservados.")) return;
                setDemoLoading(true);
                try {
                  const r = await api.post("/me/wipe-demo");
                  toast.success(`Demo removido: ${r.data.deleted_products} produtos, ${r.data.deleted_sales} vendas`);
                  setTimeout(() => window.location.reload(), 800);
                } catch (err) {
                  toast.error("Falha ao remover demo");
                } finally {
                  setDemoLoading(false);
                }
              }}
              className="border-border"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remover só dados demo
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 flex justify-end">
          <Button data-testid="profile-save-btn" type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 h-11 px-8">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </form>
    </Page>
  );
}
